import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, Package } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('loading'); // loading, success, failed, error
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollPaymentStatus = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/checkout/status/${sessionId}`);
        const data = response.data;

        setPaymentDetails(data);

        if (data.payment_status === 'paid') {
          setStatus('success');
          // Clear cart on successful payment
          localStorage.removeItem('royalInkCart');
          localStorage.removeItem('royalInkCartBackup');
        } else if (data.status === 'expired') {
          setStatus('failed');
        } else if (attempts < 10) {
          // Continue polling
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          // Max attempts reached
          setStatus('error');
        }
      } catch (err) {
        console.error('Error checking payment status:', err);
        if (attempts < 5) {
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="py-12 md:py-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-sm text-center">
            {status === 'loading' && (
              <>
                <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Loader2 className="w-10 h-10 text-amber-600 animate-spin" />
                </div>
                <h1 className="font-serif text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                  Processing Your Payment
                </h1>
                <p className="text-gray-500">
                  Please wait while we confirm your payment...
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-10 h-10 text-green-600" />
                </div>
                <h1 className="font-serif text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                  Order Confirmed!
                </h1>
                <p className="text-gray-500 mb-6">
                  Thank you for your purchase. Your order has been successfully placed.
                </p>
                
                {paymentDetails && (
                  <div className="bg-gray-50 rounded-xl p-6 mb-8 text-left">
                    <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Package className="w-5 h-5" />
                      Order Details
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Order ID:</span>
                        <span className="font-mono text-gray-900">{sessionId?.slice(0, 20)}...</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Amount Paid:</span>
                        <span className="font-semibold text-gray-900">
                          ${(paymentDetails.amount_total / 100).toFixed(2)} {paymentDetails.currency?.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Status:</span>
                        <span className="text-green-600 font-medium">Paid</span>
                      </div>
                    </div>
                  </div>
                )}

                <p className="text-gray-500 text-sm mb-6">
                  A confirmation email will be sent to you shortly. If you have any questions, please contact us at{' '}
                  <a href="mailto:aj@adroyalink.com" className="text-amber-600 hover:underline">
                    aj@adroyalink.com
                  </a>
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/shop">
                    <Button className="bg-amber-500 hover:bg-amber-600 text-gray-900 px-8 py-5 font-semibold rounded-lg">
                      Continue Shopping
                    </Button>
                  </Link>
                  <Link to="/">
                    <Button variant="outline" className="border-gray-300 text-gray-700 px-8 py-5 font-semibold rounded-lg">
                      Back to Home
                    </Button>
                  </Link>
                </div>
              </>
            )}

            {status === 'failed' && (
              <>
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-10 h-10 text-red-600" />
                </div>
                <h1 className="font-serif text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                  Payment Failed
                </h1>
                <p className="text-gray-500 mb-6">
                  Your payment session has expired or was cancelled. Your cart items have been saved.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/cart">
                    <Button className="bg-amber-500 hover:bg-amber-600 text-gray-900 px-8 py-5 font-semibold rounded-lg">
                      Return to Cart
                    </Button>
                  </Link>
                  <Link to="/">
                    <Button variant="outline" className="border-gray-300 text-gray-700 px-8 py-5 font-semibold rounded-lg">
                      Back to Home
                    </Button>
                  </Link>
                </div>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Package className="w-10 h-10 text-amber-600" />
                </div>
                <h1 className="font-serif text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                  Payment Status Unknown
                </h1>
                <p className="text-gray-500 mb-6">
                  We couldn't verify your payment status. If you completed the payment, please check your email for confirmation or contact us.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a href="mailto:aj@adroyalink.com">
                    <Button className="bg-amber-500 hover:bg-amber-600 text-gray-900 px-8 py-5 font-semibold rounded-lg">
                      Contact Support
                    </Button>
                  </a>
                  <Link to="/">
                    <Button variant="outline" className="border-gray-300 text-gray-700 px-8 py-5 font-semibold rounded-lg">
                      Back to Home
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CheckoutSuccessPage;
